import React, {useMemo} from 'react';

import {skipToken} from '@reduxjs/toolkit/dist/query';

import {GenericDiffer} from 'app/components/general/GenericDiffer';
import {usePolicyVersionById} from 'app/policies/api';
import {getContentFromPolicy} from 'app/policies/components/PolicyEditPage/utils';
import {
  getComparedVersion,
  getHtmlDiff,
  getListsDiff,
} from 'app/utils/showdiff';

import type {
  PolicyContentType,
  PolicyContentWithDiffType,
} from 'app/policies/types';
import type {ReactElement} from 'react';
import type {
  AttachedEmbed,
  AttachedMedia,
  ListPolicyVersionSerializedType,
} from 'sweet-types';

type Props = {
  location: {
    query: {
      hidediff?: string;
      'expanded-versions'?: string;
      'selected-version-group'?: string;
    };
  };
  content: PolicyContentType & {url: string; html_url: string};
  versions: Array<ListPolicyVersionSerializedType>;
  versionAvailable: boolean;
  children: (
    content: PolicyContentWithDiffType,
    showingDiff: boolean,
    toggleDiff: () => void
  ) => ReactElement;
};

export const PolicyDiffer = ({
  location,
  children,
  content,
  versionAvailable,
  versions,
}: Props) => {
  const comparedVersion = useMemo(
    () =>
      getComparedVersion(
        content.url,
        versions,
        location.query['selected-version-group']
      ),
    [content.url, versions, location.query]
  );
  const {data: compared} = usePolicyVersionById(
    comparedVersion ? comparedVersion.id : skipToken,
    {
      refetchOnMountOrArgChange: false,
    }
  );

  const diff = useMemo(() => {
    const prevVersion: PolicyContentType =
      getContentFromPolicy(compared) || getBlankVersion();
    return {
      ...content,
      name: getHtmlDiff(prevVersion.name, content.name),
      content: getHtmlDiff(prevVersion.content, content.content, false),
      files: getFileDiff(prevVersion.files || [], content.files || []),
      videos: getImagesDiff(prevVersion.videos || [], content.videos || []),
      embeds: getEmbedsDiff(prevVersion.embeds || [], content.embeds || []),
    };
  }, [content, compared]);

  return (
    <GenericDiffer
      location={location}
      versionAvailable={versionAvailable}
      content={content}
      getDiffContent={() => diff}
    >
      {children}
    </GenericDiffer>
  );
};

const getBlankVersion = (): PolicyContentType =>
  ({
    url: '',
    html_url: '',
    name: '',
    content: '',
    attachments: {
      files: [],
      videos: [],
      embeds: [],
    },
  } as any as PolicyContentType);

function getImagesDiff(
  oldImages: Array<AttachedMedia>,
  images: Array<AttachedMedia>
) {
  return getListsDiff<AttachedMedia, AttachedMedia>(
    oldImages,
    images,
    (image) => (image.image_ref != null ? image.image_ref : image.embed_thumb)
  );
}

function getEmbedsDiff(
  oldEmbeds: Array<AttachedEmbed>,
  embeds: Array<AttachedEmbed>
) {
  return getListsDiff<AttachedEmbed, AttachedEmbed>(
    oldEmbeds,
    embeds,
    (embed) => embed.embed_html
  );
}

function getFileDiff<
  T extends {
    file_ref: string;
  },
  R extends {
    changedState?: string;
    file_ref: string;
  }
>(oldList: Array<T>, newList: Array<T>) {
  return getListsDiff<T, R>(oldList, newList, (file) => file.file_ref);
}
